import React, { useState, useEffect, useRef } from 'react';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { Card } from 'primereact/card';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { InputMask } from 'primereact/inputmask';
import { InputTextarea } from 'primereact/inputtextarea';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Tag } from 'primereact/tag';
import BranchService from '../../services/BranchService';

export default function BranchesPage() {
    const [mounted, setMounted] = useState(false);
    useEffect(() => { setMounted(true); }, []);

    const [branches, setBranches] = useState([]);
    const [loading, setLoading] = useState(false);
    const [dialogVisible, setDialogVisible] = useState(false);
    const [branch, setBranch] = useState({ id: 0, name: '', phone: '', address: '', isActive: true });
    const [isEdit, setIsEdit] = useState(false);

    const toast = useRef(null);

    const loadBranches = async () => {
        try {
            setLoading(true);
            const res = await BranchService.getAll();
            const branchList = Array.isArray(res) ? res : (res?.data || []);
            setBranches(branchList);
        } catch (error) {
            console.error("Şubeler yüklenirken hata oluştu:", error);
            toast.current?.show({ severity: 'error', summary: 'Hata', detail: 'Şubeler yüklenemedi.' });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadBranches();
    }, []);

    const openNew = () => {
        setBranch({ id: 0, name: '', phone: '', address: '', isActive: true });
        setIsEdit(false);
        setDialogVisible(true);
    };

    const editBranch = (selected) => {
        setBranch({ ...selected });
        setIsEdit(true);
        setDialogVisible(true);
    };

    const hideDialog = () => {
        setDialogVisible(false);
    };

    const saveBranch = async () => {
        if (!branch.name.trim()) {
            toast.current?.show({ severity: 'warn', summary: 'Eksik Bilgi', detail: 'Şube adı zorunludur.' });
            return;
        }

        try {
            setLoading(true);
            let res;
            if (isEdit) {
                res = await BranchService.update({
                    id: branch.id,
                    name: branch.name,
                    phone: branch.phone,
                    address: branch.address,
                    isActive: branch.isActive
                });
            } else {
                res = await BranchService.create({
                    name: branch.name,
                    phone: branch.phone,
                    address: branch.address,
                    isActive: branch.isActive
                });
            }

            const success = res?.success || res?.data?.success || false;
            const message = res?.message || res?.data?.message || 'İşlem tamamlandı.';

            if (success || res?.id || typeof res === 'number') {
                toast.current?.show({ severity: 'success', summary: 'Başarılı', detail: isEdit ? 'Şube güncellendi.' : 'Şube oluşturuldu.' });
                setDialogVisible(false);
                loadBranches();
            } else {
                toast.current?.show({ severity: 'error', summary: 'Hata', detail: message });
            }
        } catch (error) {
            console.error("Şube kaydedilirken hata:", error);
            toast.current?.show({ severity: 'error', summary: 'Hata', detail: 'Kaydederken bir hata oluştu.' });
        } finally {
            setLoading(false);
        }
    };

    const confirmDelete = (selected) => {
        confirmDialog({
            message: `"${selected.name}" şubesini silmek istediğinize emin misiniz? Bu işlem geri alınamaz.`,
            header: 'Şubeyi Sil',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Evet, Sil',
            rejectLabel: 'Vazgeç',
            acceptClassName: 'p-button-danger rounded-xl',
            rejectClassName: 'p-button-text rounded-xl',
            accept: async () => {
                try {
                    setLoading(true);
                    const res = await BranchService.delete(selected.id);
                    const success = res?.success || res?.data?.success || false;
                    const message = res?.message || res?.data?.message || 'Şube silindi.';

                    if (success) {
                        toast.current?.show({ severity: 'success', summary: 'Başarılı', detail: 'Şube silindi.' });
                        loadBranches();
                    } else {
                        toast.current?.show({ severity: 'error', summary: 'Hata', detail: message });
                    }
                } catch (error) {
                    console.error("Şube silinirken hata:", error);
                    toast.current?.show({ severity: 'error', summary: 'Hata', detail: 'Şube silinirken bir hata oluştu.' });
                } finally {
                    setLoading(false);
                }
            }
        });
    };

    const statusBodyTemplate = (rowData) => {
        return (
            <Tag 
                value={rowData.isActive ? 'Aktif' : 'Pasif'} 
                severity={rowData.isActive ? 'success' : 'danger'} 
                className="rounded-lg px-2.5 py-1 text-[11px] font-bold"
            />
        );
    };

    const actionBodyTemplate = (rowData) => {
        return (
            <div className="flex gap-2">
                <Button 
                    icon="pi pi-pencil" 
                    className="p-button-rounded p-button-text p-button-sm !text-blue-600 hover:!bg-blue-50" 
                    onClick={() => editBranch(rowData)} 
                    tooltip="Düzenle"
                    tooltipOptions={{ position: 'bottom' }}
                />
                <Button 
                    icon="pi pi-trash" 
                    className="p-button-rounded p-button-text p-button-sm !text-red-600 hover:!bg-red-50" 
                    onClick={() => confirmDelete(rowData)} 
                    tooltip="Sil"
                    tooltipOptions={{ position: 'bottom' }}
                />
            </div>
        );
    };

    return (
        <div className="p-2 space-y-4 animate-fade-in bg-[#f8fafc] min-h-screen">
            <Toast ref={toast} appendTo={mounted ? document.body : "self"} />
            <ConfirmDialog appendTo={mounted ? document.body : "self"} />

            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                <div className="flex items-center gap-3">
                    <div className="!bg-blue-600 p-4 rounded-xl shadow-lg shadow-blue-500/20">
                        <i className="pi pi-map-marker text-white text-xl"></i>
                    </div>
                    <div>
                        <h1 className="text-2xl font-black text-slate-900 uppercase tracking-tight">Şube Yönetimi</h1>
                        <p className="text-slate-500 text-sm font-medium">Şirketinize ait şubeleri listeleyebilir, yeni şube tanımlayabilir veya düzenleyebilirsiniz.</p>
                    </div>
                </div>
                <Button 
                    label="YENİ ŞUBE EKLE" 
                    icon="pi pi-plus" 
                    className="p-button-primary rounded-xl font-bold px-5 py-3 shadow-md shadow-blue-500/10 text-sm" 
                    onClick={openNew} 
                />
            </div>

            <Card className="border-round-xl shadow-1 p-2">
                <DataTable 
                    value={branches} 
                    loading={loading} 
                    paginator 
                    rows={10} 
                    className="p-datatable-sm"
                    emptyMessage="Tanımlı şube bulunamadı."
                    responsiveLayout="scroll"
                >
                    <Column field="name" header="Şube Adı" sortable className="font-bold text-slate-800" style={{ minWidth: '14rem' }} />
                    <Column field="phone" header="Telefon" style={{ minWidth: '10rem' }} />
                    <Column field="address" header="Adres" className="text-slate-600 text-xs" style={{ minWidth: '18rem' }} />
                    <Column field="isActive" header="Durum" body={statusBodyTemplate} sortable style={{ width: '8rem' }} />
                    <Column header="İşlemler" body={actionBodyTemplate} style={{ width: '8rem' }} />
                </DataTable>
            </Card>

            <Dialog 
                visible={dialogVisible} 
                header={isEdit ? 'Şubeyi Düzenle' : 'Yeni Şube Ekle'} 
                modal 
                className="w-full max-w-lg" 
                onHide={hideDialog}
                footer={
                    <div className="flex justify-end gap-2">
                        <Button label="Vazgeç" icon="pi pi-times" className="p-button-text p-button-sm rounded-xl" onClick={hideDialog} />
                        <Button label="Kaydet" icon="pi pi-check" className="p-button-sm rounded-xl" loading={loading} onClick={saveBranch} />
                    </div>
                }
            >
                <div className="flex flex-col gap-4 p-fluid mt-2">
                    <div className="field">
                        <label htmlFor="name" className="font-bold mb-1 block text-sm text-slate-700">Şube Adı *</label>
                        <InputText 
                            id="name" 
                            value={branch.name} 
                            onChange={(e) => setBranch({ ...branch, name: e.target.value })} 
                            placeholder="Örn: Merkez Şube, Kadıköy Şubesi"
                            className="!rounded-xl border-gray-200"
                        />
                    </div>
                    <div className="field">
                        <label htmlFor="phone" className="font-bold mb-1 block text-sm text-slate-700">Telefon</label>
                        <InputMask 
                            id="phone" 
                            value={branch.phone} 
                            mask="0(999) 999 99 99" 
                            onChange={(e) => setBranch({ ...branch, phone: e.value })} 
                            placeholder="0(5xx) xxx xx xx"
                            className="!rounded-xl border-gray-200"
                        />
                    </div>
                    <div className="field">
                        <label htmlFor="address" className="font-bold mb-1 block text-sm text-slate-700">Adres</label>
                        <InputTextarea 
                            id="address" 
                            value={branch.address} 
                            onChange={(e) => setBranch({ ...branch, address: e.target.value })} 
                            rows={3} 
                            autoResize 
                            placeholder="Şube tam adresi"
                            className="!rounded-xl border-gray-200"
                        />
                    </div>
                    <div className="flex items-center gap-2 mt-2">
                        <input 
                            type="checkbox" 
                            id="isActive" 
                            checked={branch.isActive} 
                            onChange={(e) => setBranch({ ...branch, isActive: e.target.checked })} 
                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <label htmlFor="isActive" className="font-bold text-sm text-slate-700 cursor-pointerSelect select-none">Şube Aktif</label>
                    </div>
                </div>
            </Dialog>
        </div>
    );
}
